export const USER_PERMISSION_CODES = {
  VIEW: "accounts.users.view",
  MANAGE: "accounts.users.manage",
  ROLES_MANAGE: "accounts.roles.manage",
};

export const DASHBOARD_PERMISSION_CODES = {
  TEACHER_VIEW: "dashboards.teacher.view",
  SCHOOL_VIEW: "dashboards.school.view",
};

export const TEMPLATE_PERMISSION_CODES = {
  MANAGE: "rubrics.templates.manage",
};

export const OBSERVATION_PERMISSION_CODES = {
  CREATE: "observations.observations.create",
  // Not "a bigger CREATE": this is the read scope that turns "the visits I was
  // part of" into "the school's visits". The coverage endpoint demands it ON TOP
  // of dashboards.school.view, so a school-view holder without it is answered
  // 403 — anything that LINKS there has to check this too, or the click lands on
  // an error page.
  VIEW_ALL: "observations.observations.view_all",
  // School admin's takeover of any visit at their school. The only thing the
  // UI gates on it is DELETE, which the API refuses to the visit's own observer.
  MANAGE: "observations.observations.manage",
};

export const SCHEDULE_PERMISSION_CODES = {
  MANAGE: "organization.timetable.manage",
};

export const MATERIAL_PERMISSION_CODES = {
  MANAGE: "materials.manage",
};

// The only role code the frontend still references directly: "teacher" is a
// domain discriminator (teaching-fields form, capability toggle visibility).
// Everything else — labels, options, privilege flags — comes from the
// GET /auth/roles/ catalog.
export const ROLE_CODES = {
  TEACHER: "teacher",
};

const getUserPermissions = (user) =>
  Array.isArray(user?.perms) ? user.perms : [];

// `/auth/me/` sends both: a flat union across every school, and a per-school
// breakdown. They answer different questions and neither replaces the other.
const getOrgPermissions = (user, organizationUuid) => {
  const byOrg = user?.perms_by_organization;
  if (!byOrg || !organizationUuid) return null;
  const codes = byOrg[organizationUuid];
  return Array.isArray(codes) ? codes : null;
};

export const hasPermission = (user, perm) => {
  if (!perm) return false;
  if (user?.is_superuser) return true;
  return getUserPermissions(user).includes(perm);
};

// "Do they hold this AT this school?" — for anything rendered per school.
//
// `hasPermission` answers "anywhere?", which is the right question for
// navigation and route gates: a destination is reachable if it is reachable
// somewhere. It is the WRONG question for a control that acts on one school,
// because someone who administers school B and merely teaches at school A holds
// the permission — just not here.
//
// Falls back to the union when the payload carries no per-school breakdown for
// that org (an older session, or an org the user has no staff link to), so this
// never renders *less* than the server will allow.
export const hasPermissionAt = (user, perm, organizationUuid) => {
  if (!perm) return false;
  if (user?.is_superuser) return true;
  const codes = getOrgPermissions(user, organizationUuid);
  if (codes === null) return hasPermission(user, perm);
  return codes.includes(perm);
};

export const hasAnyPermission = (user, perms = []) => {
  if (!Array.isArray(perms) || perms.length === 0) return false;
  if (user?.is_superuser) return true;
  const all = getUserPermissions(user);
  return perms.some((p) => all.includes(p));
};

// Localized display label for the user's role set, e.g. the sidebar footer and
// the profile badge. `/auth/me/` roles carry server-localized `name`s.
export const roleNamesLabel = (user) =>
  (user?.roles || [])
    .map((r) => r?.name)
    .filter(Boolean)
    .join(" · ");
